import { cn } from "@-/com.npmjs.cn";
import type { TabBarControls } from "#material-top-tab-bar-props";
import { floatingSearchBubbleBottomGap } from "#floating-tab-bar-layout";
import {
  type NavigationRoute,
  type ParamListBase,
} from "@-/com.npmjs.expo-router/react-navigation";
import { BottomTabBarHeightContext } from "@-/com.npmjs.expo-router/bottom-tabs";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { PrimaryNavigationStripFadeInsetContext } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { ABOVE_KEYBOARD_BOTTOM_GAP_PX } from "@-/dev.improve.haniverse.components.above-keyboard";

import {
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  Platform,
  Pressable,
  TextInput,
  View,
  type StyleProp,
  type TextInputProps,
  type TextStyle,
} from "@-/com.npmjs.react-native";
import { KeyboardStickyView } from "@-/com.npmjs.react-native-keyboard-controller";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { useCSSVariable } from "@-/com.npmjs.uniwind";

const iosSearchInputStyle: StyleProp<TextStyle> = {
  lineHeight: 22,
  paddingBottom: 5,
  paddingTop: 3,
  verticalAlign: "middle",
};

export type FloatingTabBarRightBubble = {
  /** Initial value applied to the search input on each mount/expand cycle. */
  defaultQuery?: string;
  expanded: boolean;
  /** Icon rendered while collapsed (and the press target that expands the bubble). */
  icon: React.ReactNode;
  /** Whether the expanded input should reserve the app-wide keyboard-dismiss button's slot. */
  keyboardDismissButtonVisible?: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onQueryChange?: (query: string) => void;
  onSubmitSearch?: (query: string) => void;
  placeholder?: string;
  textInputProps?: Omit<TextInputProps, "defaultValue" | "onChangeText" | "placeholder" | "value">;
  /** Drawn inside the expanded field after the input, e.g. Search's match options. */
  trailingAccessory?: React.ReactNode;
};

const ANDROID_TEXT_FIX_PLATFORM = {
  ["android" as string]: { includeFontPadding: false } as const,
};
const androidTextFix = ANDROID_TEXT_FIX_PLATFORM[Platform.OS] ?? {};

function hslFromChannels(channels: string | number | undefined): string | undefined {
  if (channels === undefined || channels === "") {
    return undefined;
  }

  const value = String(channels);
  if (value.startsWith("hsl") || value.startsWith("#") || value.startsWith("rgb")) {
    return value;
  }

  return `hsl(${value})`;
}

type FloatingTabBarVariant = "static" | "dynamic";

/** Horizontal gap between adjacent tab bubbles. */
const tabBubbleGap = 8;

type FloatingTabBubbleProps = {
  isFocused: boolean;
  isSelected: boolean;
  label: string;
  mutedForegroundColor: string | undefined;
  onPress: () => void;
  options: TabBarControls["descriptors"][string]["options"];
  primaryForegroundColor: string | undefined;
  showTabLabel: boolean;
  variant: FloatingTabBarVariant;
};

/**
 * A single self-contained tab rendered as its own rounded "bubble". When selected the whole bubble
 * fills with the primary color; otherwise it sits on the background color. There is no sliding
 * indicator — each bubble owns its own active/inactive appearance.
 *
 * - `static`: icon stacked above a small label (iOS-native style), detached per bubble.
 * - `dynamic`: icon and label inline; the label only shows on the selected bubble.
 */
function FloatingTabBubble({
  isFocused,
  isSelected,
  label,
  mutedForegroundColor = "#737373",
  onPress,
  options,
  primaryForegroundColor = "#ffffff",
  showTabLabel,
  variant,
}: FloatingTabBubbleProps) {
  const iconColor = isSelected
    ? (primaryForegroundColor ?? "#ffffff")
    : (mutedForegroundColor ?? "#737373");

  const icon = options.tabBarIcon?.({
    color: iconColor,
    focused: isSelected,
  });

  // Static: every bubble shows its label (below the icon). Dynamic: label only on the selected
  // bubble (matching the previous expand-on-select behaviour).
  const showLabel = showTabLabel && (variant === "static" || isSelected);

  const labelClassName = cn(
    "min-w-0 text-center leading-tight font-medium",
    variant === "static" ? "text-[9px]" : "text-sm",
    isSelected ? "text-accent-foreground" : "text-muted-foreground",
  );

  return (
    <Pressable
      accessibilityLabel={options.tabBarAccessibilityLabel}
      accessibilityRole="tab"
      accessibilityState={isFocused ? { selected: true } : {}}
      className={cn(
        "elevation-4 h-12 min-w-0 items-center justify-center border shadow-md",
        variant === "static"
          ? "flex-1 gap-0.5 rounded-full px-1"
          : "flex-row gap-2 rounded-full px-4",
        isSelected ? "border-transparent bg-accent" : "border-border/70 bg-background",
      )}
      onPress={onPress}
      testID={options.tabBarButtonTestID}
    >
      {icon}
      {showLabel ? (
        <Text className={labelClassName} numberOfLines={1} style={androidTextFix}>
          {label}
        </Text>
      ) : null}
    </Pressable>
  );
}

// Match the pill's height so the bubble looks like a peer of the tab pill (not nested inside).
const rightBubbleCollapsedSize = 48;
// Outer wrapper spans the row edge-to-edge; the bubble row is inset by `mx-1` (4px).
// Anchor the bubble at the same 4px from the screen edge so it lines up with the row's right margin.
const rightBubbleEdgeOffset = 4;
const rightBubbleGap = 8;
const rightBubblePillRightReserve =
  rightBubbleEdgeOffset + rightBubbleCollapsedSize + rightBubbleGap;
const rightBubbleAnchorStyle = { right: rightBubbleEdgeOffset, top: 0 } as const;
const rightBubblePillRightStyle = { marginRight: rightBubblePillRightReserve } as const;
/**
 * How long the input takes to grow into / retreat from the dismiss button's slot. Matches the
 * platform keyboard's own show/hide curve so the input widens in step with the keyboard leaving,
 * rather than snapping to full width the instant the button unmounts.
 */
const dismissButtonSlotDurationMs = 250;
const continuousBorderCurveStyle = { borderCurve: "continuous" } as const;

export function FloatingRightSearchBubble({
  externalBubblePillWidth,
  rightFloatingBubble,
}: {
  /** Width of the tab bubble row; used to compute the bubble's expanded width. */
  externalBubblePillWidth: number;
  rightFloatingBubble: FloatingTabBarRightBubble;
}) {
  const bottomTabFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  // The navigator's own measured bar height: the global tab bar is always-attached chrome, unlike
  // this scene, which is detached whenever Search is a background tab. It reports 0 for the
  // landscape side rail.
  const tabBarHeight = useContext(BottomTabBarHeightContext) ?? 0;
  // The scene already ends above the global tabs. Account only for its fade overlap;
  // adding the device safe area again would lift the field too far above the tab row.
  const bottomOffset = floatingSearchBubbleBottomGap + bottomTabFadeInset;
  // Distance from the scene's bottom edge to the window's. The scene overlaps the bar by its fade
  // inset, and that inset is zero exactly when no bottom bar is drawn (side rail, hidden bar), where
  // the scene reaches the window edge — and where a stale bar height may linger in context.
  const sceneBottomInset = bottomTabFadeInset > 0 ? tabBarHeight - bottomTabFadeInset : 0;
  return (
    // Rides the keyboard's live position like the root dismiss button (`AboveKeyboard`), settling
    // the same `ABOVE_KEYBOARD_BOTTOM_GAP_PX` above it so the two share one row. `opened` cancels
    // the space already below the resting bubble, which the keyboard covers first.
    <KeyboardStickyView
      className="absolute inset-x-0 h-12 items-center"
      enabled={rightFloatingBubble.expanded}
      offset={{ opened: sceneBottomInset + bottomOffset - ABOVE_KEYBOARD_BOTTOM_GAP_PX }}
      pointerEvents="box-none"
      style={{ bottom: bottomOffset }}
    >
      <RightFloatingSearchBubble bubble={rightFloatingBubble} pillWidth={externalBubblePillWidth} />
    </KeyboardStickyView>
  );
}

function RightFloatingSearchBubble({
  bubble,
  pillWidth,
}: {
  bubble: FloatingTabBarRightBubble;
  pillWidth: number;
}) {
  const { diameter, edgeInset } = useToolbarGridMetrics();
  const rightBubbleExpandedInputLeftInset = diameter + 9;
  const inputRef = useRef<TextInput>(null);
  const {
    defaultQuery,
    expanded,
    icon,
    keyboardDismissButtonVisible = false,
    onExpandedChange,
    onQueryChange,
    onSubmitSearch,
    placeholder,
    textInputProps,
    trailingAccessory,
  } = bubble;
  const [query, setQuery] = useState(defaultQuery ?? "");
  const expandedProgress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    setQuery(defaultQuery ?? "");
  }, [defaultQuery]);

  useEffect(() => {
    expandedProgress.set(withTiming(expanded ? 1 : 0, { duration: 200 }));
    if (expanded) {
      // Defer focus so the layout animation begins before the keyboard opens.
      const handle = setTimeout(() => {
        inputRef.current?.focus();
      }, 60);
      return () => clearTimeout(handle);
    }
    inputRef.current?.blur();
    return undefined;
  }, [expanded, expandedProgress]);

  const containerAnimatedStyle = useAnimatedStyle(() => {
    const collapsedWidth = rightBubbleCollapsedSize;
    // Expand across the row with the same outer gutters as the answer bar and header controls.
    const expandedWidth = Math.max(
      collapsedWidth,
      pillWidth +
        rightBubbleGap +
        rightBubbleCollapsedSize -
        2 * (edgeInset - rightBubbleEdgeOffset),
    );
    const width = collapsedWidth + (expandedWidth - collapsedWidth) * expandedProgress.get();
    return { width };
  });

  // Reserved slot for the app-wide keyboard-dismiss button, animated rather than switched. The
  // button mounts/unmounts instantly on the eager keyboard signal; without this the input's left
  // edge would jump 40px in a single frame at the same moment. Timing it lets the input glide into
  // the space the button just vacated (and back out of it when the button returns).
  const dismissButtonSlotProgress = useSharedValue(keyboardDismissButtonVisible ? 1 : 0);

  useEffect(() => {
    dismissButtonSlotProgress.set(
      withTiming(keyboardDismissButtonVisible ? 1 : 0, {
        duration: dismissButtonSlotDurationMs,
      }),
    );
  }, [dismissButtonSlotProgress, keyboardDismissButtonVisible]);

  const inputContainerAnimatedStyle = useAnimatedStyle(() => ({
    left: rightBubbleExpandedInputLeftInset * dismissButtonSlotProgress.get(),
    opacity: expandedProgress.get(),
    // Pinned to the container's right edge so the input's width is purely a function of `left` —
    // animating the inset alone then resizes the field rather than sliding it.
    right: 0,
  }));

  const handlePressBubble = useCallback(() => {
    if (!expanded) {
      onExpandedChange(true);
    }
  }, [expanded, onExpandedChange]);

  const handleQueryChange = useCallback(
    (nextQuery: string) => {
      setQuery(nextQuery);
      onQueryChange?.(nextQuery);
    },
    [onQueryChange],
  );

  // Like UISearchBar's clear button: wipe the field and leave it focused for the next query.
  const handleClearQuery = useCallback(() => {
    handleQueryChange("");
    inputRef.current?.focus();
  }, [handleQueryChange]);

  return (
    <Animated.View
      className={cn(
        "absolute flex-row items-center",
        expanded
          ? "overflow-visible"
          : "elevation-4 h-12 justify-end overflow-hidden rounded-full border border-border/70 bg-background shadow-md",
      )}
      style={[
        expanded
          ? { height: diameter, right: edgeInset, top: rightBubbleCollapsedSize - diameter }
          : rightBubbleAnchorStyle,
        containerAnimatedStyle,
      ]}
    >
      <Animated.View
        className="absolute top-0 h-full flex-row items-center"
        pointerEvents={expanded ? "auto" : "none"}
        style={[continuousBorderCurveStyle, inputContainerAnimatedStyle]}
      >
        <ToolbarGlassSurface className="h-full flex-1 flex-row items-center" interactive={false}>
          {query.length > 0 ? (
            <Pressable
              accessibilityLabel="Clear search"
              accessibilityRole="button"
              className="h-full shrink-0 items-center justify-center pr-1 pl-3"
              hitSlop={{ bottom: 4, left: 4, top: 4 }}
              onPress={handleClearQuery}
              testID="search-input-clear"
            >
              <Icon className="text-muted-foreground/70" name="IoCloseCircle" size={18} />
            </Pressable>
          ) : null}
          <TextInput
            {...(Platform.OS === "android"
              ? { includeFontPadding: true, textAlignVertical: "center" as const }
              : {})}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect={false}
            className={cn(
              "h-full min-w-0 flex-1 bg-transparent font-sans text-base text-foreground placeholder:text-muted-foreground/60",
              query.length > 0 ? "pr-3 pl-1" : "px-3",
              Platform.OS === "android" && "py-0 leading-5",
              Platform.select({ web: "outline-none focus:outline-none" }),
            )}
            editable={expanded}
            onChangeText={handleQueryChange}
            onSubmitEditing={(event) => onSubmitSearch?.(event.nativeEvent.text)}
            placeholder={placeholder}
            ref={inputRef}
            returnKeyType="search"
            spellCheck={false}
            style={[
              Platform.OS === "ios" ? iosSearchInputStyle : undefined,
              continuousBorderCurveStyle,
            ]}
            testID="search-input"
            {...textInputProps}
            value={query}
          />
          {trailingAccessory}
        </ToolbarGlassSurface>
      </Animated.View>
      {!expanded ? (
        <Pressable
          accessibilityLabel="Open search"
          accessibilityRole="button"
          className="size-12 items-center justify-center"
          onPress={handlePressBubble}
        >
          {icon}
        </Pressable>
      ) : null}
    </Animated.View>
  );
}

function MaterialFloatingTabBarInner({
  bottomInset = 0,
  descriptors,
  emitter,
  hiddenRouteNames = [],
  navigateToTab,
  rightFloatingBubble,
  selectedRouteName,
  state,
  variant,
}: TabBarControls & {
  bottomInset?: number;
  /** Routes hosted by the navigator but omitted from the visible tab row. */
  hiddenRouteNames?: readonly string[];
  rightFloatingBubble?: FloatingTabBarRightBubble;
  /** Visually select this route while a hidden intermediary route is active. */
  selectedRouteName?: string;
  variant: FloatingTabBarVariant;
}) {
  const bottomTabFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  const primaryForegroundChannels = useCSSVariable("--color-accent-foreground");
  const mutedForegroundChannels = useCSSVariable("--color-muted-foreground");
  const primaryForegroundColor = hslFromChannels(primaryForegroundChannels);
  const mutedForegroundColor = hslFromChannels(mutedForegroundChannels);

  const routeList = useMemo((): NavigationRoute<ParamListBase, string>[] => {
    return state.routes.filter(
      (route: NavigationRoute<ParamListBase, string>) =>
        !["_sitemap", "+not-found", ...hiddenRouteNames].includes(route.name),
    );
  }, [hiddenRouteNames, state.routes]);

  const focusedRouteKey = state.routes[state.index].key;

  return (
    <View
      className="absolute inset-x-0 h-12 items-center"
      pointerEvents="box-none"
      style={{ bottom: 4 + bottomInset + bottomTabFadeInset }}
    >
      <View
        className={cn(
          "h-12 flex-row",
          rightFloatingBubble === undefined ? "mx-1" : "ml-1",
          // Static bubbles are `flex-1`, so the row must stretch to the full available width. When a
          // right bubble reserves space the row must also stretch (it sizes the reserved slot).
          // A dynamic row with no right bubble hugs its content so the parent's `items-center`
          // centers it — keeping the margin past the last (possibly selected/expanded) bubble equal
          // to the margin before the first.
          variant === "static" || rightFloatingBubble !== undefined
            ? "self-stretch"
            : "self-center",
        )}
        style={[
          { gap: tabBubbleGap },
          rightFloatingBubble === undefined ? undefined : rightBubblePillRightStyle,
        ]}
      >
        {routeList.map((route) => {
          const descriptor = descriptors[route.key];
          const { options } = descriptor;
          const isFocused =
            selectedRouteName === undefined
              ? route.key === focusedRouteKey
              : route.name === selectedRouteName;
          if (__DEV__ && options.tabBarIcon === undefined) {
            throw new Error(
              `FloatingTabBar: route "${route.name}" is missing a \`tabBarIcon\` option. ` +
                `Every tab must provide an icon (both static and dynamic variants render it).`,
            );
          }
          const showTabLabel = options.tabBarShowLabel !== false;
          const label =
            typeof options.tabBarLabel === "string"
              ? options.tabBarLabel
              : (options.title ?? route.name);

          return (
            <FloatingTabBubble
              isFocused={isFocused}
              isSelected={isFocused}
              key={route.key}
              label={label}
              mutedForegroundColor={mutedForegroundColor}
              onPress={() => {
                const event = emitter.emit({
                  canPreventDefault: true,
                  target: route.key,
                  type: "tabPress",
                });

                if (event.defaultPrevented) {
                  return;
                }

                if (!isFocused) {
                  navigateToTab(route.key);
                }
              }}
              options={options}
              primaryForegroundColor={primaryForegroundColor}
              showTabLabel={showTabLabel}
              variant={variant}
            />
          );
        })}
      </View>
    </View>
  );
}

/** Equal-width tab bubbles side by side; the selected bubble fills with the primary color. */
export function FloatingStaticTabBar(
  props: TabBarControls & {
    bottomInset?: number;
    hiddenRouteNames?: readonly string[];
    rightFloatingBubble?: FloatingTabBarRightBubble;
    selectedRouteName?: string;
  },
) {
  return <MaterialFloatingTabBarInner variant="static" {...props} />;
}

/** Tab bubbles sized to their content; each shows its `tabBarIcon`, and the selected bubble also reveals its label. */
export function DynamicIconsTabBar(
  props: TabBarControls & {
    bottomInset?: number;
    hiddenRouteNames?: readonly string[];
    rightFloatingBubble?: FloatingTabBarRightBubble;
    selectedRouteName?: string;
  },
) {
  return <MaterialFloatingTabBarInner variant="dynamic" {...props} />;
}
